import axios from "../../../api/axios";
import { drawingKindLabel } from "./drawingKinds";

export const fetchProjectDrawings = async (projectId) => {
  if (!projectId) return [];
  const response = await axios.get("/drawings/", {
    params: {
      project_id: projectId,
      per_page: 100,
      page: 1,
      fields: "id,title,project,floor,house,drawing_kind,drawing_floor_id,drawing_floor,updated_at",
    },
  });
  return Array.isArray(response.data?.data) ? response.data.data : [];
};

export const fetchDrawingHouses = async (projectId) => {
  const response = await axios.get("/drawing_houses", {
    params: projectId ? { project_id: projectId } : {},
  });
  return response.data?.data || [];
};

export const createDrawingHouse = async ({ name, projectId }) => {
  const response = await axios.post("/drawing_houses", {
    drawing_house: {
      name,
      project_id: projectId || null,
    },
  });
  return response.data;
};

export const createDrawingFloor = async (houseId, name) => {
  const response = await axios.post(`/drawing_houses/${houseId}/drawing_floors`, {
    drawing_floor: { name },
  });
  return response.data;
};

export const updateDrawingHouse = async (id, payload) => {
  const response = await axios.put(`/drawing_houses/${id}`, {
    drawing_house: payload,
  });
  return response.data;
};

export const updateDrawingFloor = async (id, payload) => {
  const response = await axios.put(`/drawing_floors/${id}`, {
    drawing_floor: payload,
  });
  return response.data;
};

export const deleteDrawingHouse = async (id) => {
  await axios.delete(`/drawing_houses/${id}`);
};

export const deleteDrawingFloor = async (id) => {
  await axios.delete(`/drawing_floors/${id}`);
};

export const findFloorInStructure = (houses, floorId) => {
  if (!floorId) return null;
  for (const house of houses || []) {
    const floor = (house.floors || []).find((item) => item.id === floorId);
    if (floor) {
      return { house, floor };
    }
  }
  return null;
};

export const formatDrawingLocation = (drawing) => {
  const house =
    drawing?.drawing_floor?.drawing_house?.name || drawing?.house || "";
  const floor = drawing?.drawing_floor?.name || drawing?.floor || "";
  return { house, floor };
};

/** Labeled "Hus X · Etasje Y · Type" (or Andre · Type) for switcher menus. */
export const formatDrawingLocationMeta = (t, drawing, { includeProject = false } = {}) => {
  const { house, floor } = formatDrawingLocation(drawing);
  const parts = [];
  if (includeProject && drawing?.project?.displayTitle) {
    parts.push(drawing.project.displayTitle);
  }
  // Treat as "Andre" when there is no actual floor assignment id, regardless of legacy strings.
  const hasFloorId = Boolean(drawing?.drawing_floor?.id || drawing?.drawing_floor_id);
  if (!hasFloorId) {
    parts.push(t("drawings.structure.andre"));
  } else {
    if (house) parts.push(`${t("drawings.house")} ${house}`);
    if (floor) parts.push(`${t("drawings.floor")} ${floor}`);
  }
  // Always show type label; fallback handled by drawingKindLabel
  parts.push(drawingKindLabel(t, drawing?.drawing_kind));
  return parts.join(" · ");
};

/** Format drawing/version timestamps as DD.MM.YYYY for the viewer title meta line. */
export const formatDrawingUpdatedAt = (value) => {
  if (!value) return null;
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  const dd = String(date.getDate()).padStart(2, "0");
  const mm = String(date.getMonth() + 1).padStart(2, "0");
  const yyyy = date.getFullYear();
  return `${dd}.${mm}.${yyyy}`;
};

/**
 * Meta under the drawing title, split for layout:
 * - locationLine: project · house · floor
 * - revisionLine: Rev./Live · updated DD.MM.YYYY
 * Uses active markup version title when in version mode; otherwise Live.
 * Timestamp prefers active version updated_at/created_at, else drawing.updated_at.
 */
export const buildDrawingViewerTitleMeta = ({
  t,
  drawing,
  isVersionMode = false,
  activeVersion = null,
  versions = [],
}) => {
  const { house, floor } = formatDrawingLocation(drawing);

  let revisionLabel = t("drawings.viewer.live_drawing");
  if (isVersionMode && activeVersion) {
    const sorted = [...(versions || [])].sort(
      (a, b) => new Date(a.created_at || 0) - new Date(b.created_at || 0)
    );
    const index = sorted.findIndex(
      (version) => String(version.id) === String(activeVersion.id)
    );
    const name =
      activeVersion.title ||
      (index >= 0 ? String(index + 1) : String(activeVersion.id));
    revisionLabel = t("drawings.viewer.revision", { name });
  }

  const updatedRaw =
    (isVersionMode && activeVersion
      ? activeVersion.updated_at || activeVersion.created_at
      : null) || drawing?.updated_at;
  const updatedDate = formatDrawingUpdatedAt(updatedRaw);
  const updatedLabel = updatedDate
    ? t("drawings.viewer.updated_on", { date: updatedDate })
    : null;

  // Prefer hus + etasje (or Andre) + kind; project is secondary when present.
  const hasFloorId2 = Boolean(drawing?.drawing_floor?.id || drawing?.drawing_floor_id);
  const locationParts = [
    !hasFloorId2 ? t("drawings.structure.andre") : null,
    hasFloorId2 && house ? `${t("drawings.house")} ${house}` : null,
    hasFloorId2 && floor ? `${t("drawings.floor")} ${floor}` : null,
    // Always show type label; fallback handled by drawingKindLabel
    drawingKindLabel(t, drawing?.drawing_kind),
    drawing?.project?.displayTitle,
  ].filter(Boolean);
  const revisionParts = [revisionLabel, updatedLabel].filter(Boolean);

  return {
    locationParts,
    revisionParts,
    locationLine: locationParts.join(" · "),
    revisionLine: revisionParts.join(" · "),
  };
};
